/* eslint-disable no-unused-vars */
import { PublicClientApplication, InteractionStatus, BrowserCacheLocation } from '@azure/msal-browser';
import { aadTenantId, aadClientId, aadRedirectUri, aadResourceId } from '@/environment/environment'

const config = {
  tenant: aadTenantId,
  clientId: aadClientId,
  redirectUri: aadRedirectUri,
  webApiResourceId: aadResourceId
};

class AuthService {
  static instance = null;

  constructor() {
    // Implement singleton pattern
    if (AuthService.instance) {
      return AuthService.instance;
    }

    const msalConfig = {
      auth: {
        clientId: config.clientId,
        authority: 'https://login.microsoftonline.com/' + config.tenant,
        redirectUri: config.redirectUri,
        navigateToLoginRequestUrl: false
      },
      cache : {
        cacheLocation: BrowserCacheLocation.LocalStorage
      },
      system: {
        allowRedirectInIframe: false
      }
    };

    this.msalInstance = new PublicClientApplication(msalConfig);
    this.redirectPromise = null;
    this.inProgress = false;
    
    // Listen to MSAL events
    this.msalInstance.addEventCallback((event) => {
      console.debug('MSAL Event:', event.eventType, event);
      
      // Update inProgress based on event type
      if (event.eventType === 'msal:loginStart' || 
          event.eventType === 'msal:acquireTokenStart' ||
          event.eventType === 'msal:handleRedirectStart') {
        this.inProgress = true;
      }
      
      if (event.eventType === 'msal:loginSuccess' || 
          event.eventType === 'msal:loginFailure' ||
          event.eventType === 'msal:acquireTokenSuccess' ||
          event.eventType === 'msal:acquireTokenFailure' ||
          event.eventType === 'msal:handleRedirectEnd') {
        this.inProgress = false;
      }
    });

    // Store singleton instance
    AuthService.instance = this;
  }
  
  isLoginInProgress() {
    // MSAL's built-in interaction status check
    return this.msalInstance.getInteractionStatus() !== InteractionStatus.None;
  }

  // Or just use the flag
  getInProgress() {
    return this.inProgress;
  }

  initialize() {
    if (this.redirectPromise) {
      return this.redirectPromise;
    }
    
    this.redirectPromise = this.msalInstance.initialize().then(() => {
      return this.msalInstance.handleRedirectPromise().then(
        (response) => {
          if (response && response.account) {
            this.msalInstance.setActiveAccount(response.account);
            this.successCallback(response);
          } else {
            // Check if there are any accounts in cache
            const accounts = this.msalInstance.getAllAccounts();
            if (accounts.length > 0) {
              this.msalInstance.setActiveAccount(accounts[0]);
            }
          }
          return response;
        },
        (error) => {
          console.error('Redirect error:', error);
          this.failCallBack(error);
          throw error;
        }
      );
    });

    return this.redirectPromise;
  }
  
  login() {
    const loginRequest = {
      scopes: [
        "user.read",
        "https://analysis.windows.net/powerbi/api/Report.Read.All",
        config.webApiResourceId + "/user_impersonation"
      ] 
    };
    return this.msalInstance.loginPopup(loginRequest).then(
      response => {
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          return accounts[0];
        } else {
          return null;
        }
      },
      (e) => {
        // eslint-disable-next-line no-console
        console.error('Login error:', e);
        return null;
      }
    );
  }
  loginRedirect() {
    const loginRequest = {
      scopes: [
        config.webApiResourceId + "/user_impersonation"
      ] 
    };
    this.msalInstance.loginRedirect(loginRequest);
  }
  logout() {
    this.msalInstance.logout();
  }
  successCallback(response) {
    if (response && response.idTokenClaims) {
      this.userRoles = response.idTokenClaims.roles;
    }
  }

  failCallBack(error) {
    //thisObj.setState({ error: ["Redirect error: " + error] });
  }
  getMsGraphToken() {
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return Promise.reject('No accounts found');
    }
    const tokenRequest = {
      scopes: ["user.read"],
      account: accounts[0]
    };
    return this.msalInstance.acquireTokenSilent(tokenRequest).then(
      response => {
        return response;
      },
      error => {
        return this.msalInstance.acquireTokenRedirect(tokenRequest);
      }
    );
  }
  getPowerBiToken() {
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return Promise.reject('No accounts found');
    }
    const tokenRequest = {
      scopes: ["https://analysis.windows.net/powerbi/api/Report.Read.All"],
      account: accounts[0]
    };
    return this.msalInstance.acquireTokenSilent(tokenRequest).then(
      response => {
        return response;
      },
      error => {
        return this.msalInstance.acquireTokenRedirect(tokenRequest);
      }
    );
  }
  getWebApiToken() {
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return Promise.reject('No accounts found');
    }
    const tokenRequest = {
      scopes: [config.webApiResourceId + "/user_impersonation"],
      account: accounts[0]
    };
    return this.msalInstance.acquireTokenSilent(tokenRequest).then(
      response => {
        return response;
      },
      error => {
        // eslint-disable-next-line no-console
        console.error(error, this.isAuthenticated());
        return this.msalInstance.acquireTokenRedirect(tokenRequest);
      }
    );
  }
  getUser() {
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }
  isInRole(role) {
    if (this.isAuthenticated()) {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const userRoles = accounts[0].idTokenClaims.roles;
        if (role && userRoles) {
          return userRoles.includes(role);
        }
      }
    }
    return false;
  }
  checkRoles(roles) {
    if (this.isAuthenticated()) {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const userRoles = accounts[0].idTokenClaims.roles;
        if (userRoles) {
          return roles.some(role => userRoles.includes(role));
        }
      }
    }
    return false;
  }
  isAuthenticated() {
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0;
  }
  
}

export default AuthService;